/**
 * History Import Page
 * Import spreadsheets of ASO/exam history and training history completed
 * before the platform's adoption.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Chip, Grid, Typography } from '@mui/material';
import {
  MedicalServices as ExamIcon,
  WorkspacePremium as TrainingIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { PageShell, SectionCard, DataTable, type DataTableColumn } from '../../components';
import { usePermissions } from '../../hooks/usePermissions';
import AppointmentHistoryImportDialog from './components/AppointmentHistoryImportDialog';
import TrainingHistoryImportDialog from './components/TrainingHistoryImportDialog';
import PdfHistoryImportDialog from './components/PdfHistoryImportDialog';
import { historyImportLogService, type HistoryImportLogView } from '../../services/historyImportLogService';
import branding from '../../config/branding';
import { appointmentHistoryImportService } from '../../services/appointmentHistoryImportService';
import { trainingHistoryImportService } from '../../services/trainingHistoryImportService';

const TYPE_LABELS: Record<HistoryImportLogView['type'], string> = {
  aso: 'ASO/Exame',
  training: 'Treinamento',
};

const logColumns: DataTableColumn<HistoryImportLogView>[] = [
  {
    id: 'type',
    label: 'Tipo',
    primary: true,
    render: (log) => (
      <Chip
        size="small"
        label={TYPE_LABELS[log.type]}
        color={log.type === 'aso' ? 'primary' : 'secondary'}
        variant="outlined"
      />
    ),
  },
  { id: 'fileName', label: 'Arquivo', truncate: true, width: 220, render: (log) => log.fileName },
  { id: 'imported', label: 'Importados', render: (log) => log.imported },
  {
    id: 'errorsCount',
    label: 'Erros',
    render: (log) => (log.errorsCount > 0
      ? <Chip size="small" color="error" label={log.errorsCount} />
      : log.errorsCount),
  },
  { id: 'userName', label: 'Quem importou', render: (log) => log.userName },
  {
    id: 'createdAt',
    label: 'Quando',
    render: (log) => new Date(log.createdAt).toLocaleString('pt-BR'),
  },
];

export const HistoryImportPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [trainingDialogOpen, setTrainingDialogOpen] = useState(false);
  const [appointmentPdfDialogOpen, setAppointmentPdfDialogOpen] = useState(false);
  const [trainingPdfDialogOpen, setTrainingPdfDialogOpen] = useState(false);
  const [logs, setLogs] = useState<HistoryImportLogView[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const canImportAppointments = hasPermission('appointment.import');
  const canImportTrainings = hasPermission('training.import');
  const canSeeLogs = canImportAppointments || canImportTrainings;

  const refreshLogs = useCallback(() => {
    if (!canSeeLogs) return;
    setLogsLoading(true);
    historyImportLogService.list({ limit: 20 })
      .then(setLogs)
      .catch(() => {})
      .finally(() => setLogsLoading(false));
  }, [canSeeLogs]);

  useEffect(() => {
    refreshLogs();
  }, [refreshLogs]);

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Importador de Histórico' },
      ]}
      title="Importador de Histórico"
      subtitle="Importe planilhas de ASO/exames e de treinamentos já concluídos, anteriores à adoção do sistema"
    >
      <Grid container spacing={3}>
        {canImportAppointments && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="ASO e Exames" icon={<ExamIcon />}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Importe o histórico de ASOs e exames ocupacionais já realizados antes da
                adoção do {branding.appName}, para manter o prontuário dos funcionários completo.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setAppointmentDialogOpen(true)}
                >
                  Importar Histórico
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => setAppointmentPdfDialogOpen(true)}
                >
                  Importar por PDF
                </Button>
              </Box>
            </SectionCard>
          </Grid>
        )}

        {canImportTrainings && (
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title="Treinamentos" icon={<TrainingIcon />}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Importe o histórico de treinamentos já concluídos antes da adoção do
                {branding.appName}, para manter os registros de capacitação completos.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setTrainingDialogOpen(true)}
                >
                  Importar Histórico
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => setTrainingPdfDialogOpen(true)}
                >
                  Importar por PDF
                </Button>
              </Box>
            </SectionCard>
          </Grid>
        )}
      </Grid>

      {canSeeLogs && (
        <SectionCard
          title="Importações Recentes"
          icon={<HistoryIcon />}
          badge={<Chip size="small" label={logs.length} />}
        >
          <DataTable
            columns={logColumns}
            rows={logs}
            getRowId={(log) => log.id}
            loading={logsLoading}
            emptyState={{
              title: 'Nenhuma importação de histórico registrada ainda',
              description: 'Assim que você importar uma planilha de ASO/exames ou treinamentos, a tentativa aparece aqui.',
            }}
          />
        </SectionCard>
      )}

      {/*
        onImportComplete dispara sempre que imported > 0, mesmo com erros na mesma
        importação (é o sinal de "algo mudou", pensado pra dar refresh numa listagem).
        Aqui não há lista pra atualizar, e fechar o dialog nesse callback esconderia a
        lista de erros do usuário antes que ele pudesse ler. Quem decide fechar o dialog
        é o próprio ImportWithMappingDialog (shouldClose interno, só quando 100% dos
        registros importaram sem erro) ou o usuário clicando em Cancelar/X.
        onLogsShouldRefresh é separado: só busca de novo a lista de "Importações
        Recentes" acima, sem interferir no fechamento do dialog.
      */}
      <AppointmentHistoryImportDialog
        open={appointmentDialogOpen}
        onClose={() => setAppointmentDialogOpen(false)}
        onImportComplete={() => {}}
        onLogsShouldRefresh={refreshLogs}
      />

      <TrainingHistoryImportDialog
        open={trainingDialogOpen}
        onClose={() => setTrainingDialogOpen(false)}
        onImportComplete={() => {}}
        onLogsShouldRefresh={refreshLogs}
      />

      <PdfHistoryImportDialog
        open={appointmentPdfDialogOpen}
        onClose={() => setAppointmentPdfDialogOpen(false)}
        title="Importar ASO/Exames por PDF"
        description="Selecione um ou mais PDFs de ASO/exame já concluídos — a IA extrai os dados de cada arquivo automaticamente, sem precisar de planilha."
        onImport={(files, options) => appointmentHistoryImportService.importFromPdfs(files, options)}
        onImportComplete={refreshLogs}
      />

      <PdfHistoryImportDialog
        open={trainingPdfDialogOpen}
        onClose={() => setTrainingPdfDialogOpen(false)}
        title="Importar Treinamentos por PDF"
        description="Selecione um ou mais PDFs de certificado de treinamento já concluído — a IA extrai os dados de cada arquivo automaticamente, sem precisar de planilha."
        onImport={(files, options) => trainingHistoryImportService.importFromPdfs(files, options)}
        onImportComplete={refreshLogs}
      />
    </PageShell>
  );
};

export default HistoryImportPage;
